import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Turnstile as TurnstileWidget } from "@marsidev/react-turnstile";

// Convention: whoever wires this component into a form must send the
// resulting token as this header on that form's API call. The component
// itself is not tied to any endpoint.
export const TURNSTILE_TOKEN_HEADER = "X-Turnstile-Token";

/**
 * Reusable Cloudflare Turnstile widget.
 *
 * Not wired to any form/endpoint - the caller owns the token via onVerify
 * and is responsible for sending it as the `X-Turnstile-Token` header
 * (see TURNSTILE_TOKEN_HEADER) on the relevant API call.
 *
 * Modes:
 * - "visible": renders the standard checkbox widget on mount.
 * - "invisible": renders no checkbox and does nothing until the parent
 *   calls `ref.current.execute()` (e.g. on form submit). Cloudflare may
 *   still show an interactive challenge if it deems it necessary.
 *
 * The ref exposes the underlying widget instance (execute/reset/getResponse/...)
 * for cases like resetting after a failed submit or manually triggering
 * execution in invisible mode.
 *
 * @param {Object} props
 * @param {(token: string) => void} props.onVerify - called with the challenge token once solved.
 * @param {() => void} [props.onExpire] - called when a previously issued token expires.
 * @param {(errorCode: string) => void} [props.onError] - called when the widget fails to load/verify.
 * @param {"visible"|"invisible"} [props.mode]
 * @param {"auto"|"light"|"dark"} [props.theme]
 * @param {"normal"|"compact"|"flexible"} [props.size] - ignored in invisible mode.
 * @param {string} [props.className]
 */
const Turnstile = forwardRef(
  (
    {
      onVerify,
      onExpire,
      onError,
      mode = "visible",
      theme = "auto",
      size = "normal",
      className,
    },
    forwardedRef
  ) => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    const widgetRef = useRef(null);

    useImperativeHandle(forwardedRef, () => widgetRef.current, []);

    if (!siteKey) {
      console.error(
        "Turnstile: key is not set, widget cannot render."
      );
      return null;
    }

    const isInvisible = mode === "invisible";

    return (
      <TurnstileWidget
        ref={widgetRef}
        siteKey={siteKey}
        className={className}
        options={{
          theme,
          size: isInvisible ? "invisible" : size,
          execution: isInvisible ? "execute" : "render",
          appearance: isInvisible ? "interaction-only" : "always",
        }}
        onSuccess={onVerify}
        onExpire={onExpire}
        onError={onError}
      />
    );
  }
);

Turnstile.displayName = "Turnstile";

export default Turnstile;
